import { _ as __rest, a as __assign } from './tslib.es6-01f7d394.js';
import { c as classNames } from './index-8a7d9ef9.js';
import { a as useSelect, b as useMultipleSelection } from './downshift.esm-965667fc.js';
import React__default, { useState, useEffect, useCallback } from 'react';
import { s as selectStyles } from './SelectInput.module-3fe53ec9.js';
import { s as styles } from './FilterSelect.module-4a34c570.js';
import { P as Popover } from './Popover-7e83b807.js';
import { W as WithFocusTrap } from './WithFocusTrap-ab2093dd.js';
import { C as Checkbox } from './Checkbox-97eb168a.js';
import { B as Button } from './Button-10a5e1ea.js';
import { T as Typography } from './Typography-cdf537a9.js';
import { A as ArrowDownIcon } from './ArrowDownIcon-59ff3a4e.js';

var FilterSelect = React__default.forwardRef(function FilterSelect(_a, ref) {
    var _b;
    var label = _a.label, items = _a.items, className = _a.className, _c = _a["data-testid"], testId = _c === void 0 ? "filter-select" : _c, _d = _a.trapFocus, trapFocus = _d === void 0 ? true : _d, disabled = _a.disabled, isSkeleton = _a.isSkeleton, itemClassName = _a.itemClassName, itemLabelClassName = _a.itemLabelClassName, extraItemLabelClassName = _a.extraItemLabelClassName, labelGroupClassName = _a.labelGroupClassName, customSelectedValueContent = _a.customSelectedValueContent, _e = _a.popoverSize, popoverSize = _e === void 0 ? "parent" : _e, props = __rest(_a, ["label", "items", "className", "data-testid", "trapFocus", "disabled", "isSkeleton", "itemClassName", "itemLabelClassName", "extraItemLabelClassName", "labelGroupClassName", "customSelectedValueContent", "popoverSize"]);
    var _f = {
        handleSelectedItemChange: props.type === "single" ? props.handleSelectedItemChange : undefined,
        initialSelectedItem: props.type === "single" ? props.initialSelectedItem : undefined,
        selectedItemProp: props.type === "single" ? props.selectedItem : undefined,
        handleSelectedItemsChange: props.type === "multi" ? props.handleSelectedItemsChange : undefined,
        initialSelectedItems: props.type === "multi" ? props.initialSelectedItems : undefined,
        selectedItemsProp: props.type === "multi" ? props.selectedItems : undefined,
        minimumSelectedItems: props.type === "multi" ? props.minimumSelectedItems : undefined,
    }, handleSelectedItemChange = _f.handleSelectedItemChange, initialSelectedItem = _f.initialSelectedItem, selectedItemProp = _f.selectedItemProp, handleSelectedItemsChange = _f.handleSelectedItemsChange, initialSelectedItems = _f.initialSelectedItems, selectedItemsProp = _f.selectedItemsProp, minimumSelectedItems = _f.minimumSelectedItems;
    var _g = useState(items.filter(function (i) {
        return initialSelectedItems === null || initialSelectedItems === void 0 ? void 0 : initialSelectedItems.some(function (s) { return s.value === i.value; });
    }) || []), selectedItemsState = _g[0], setSelectedItemsState = _g[1];
    useEffect(function () {
        if (selectedItemsProp) {
            setSelectedItemsState(selectedItemsProp);
        }
    }, [selectedItemsProp]);
    var stateReducer = useCallback(function (state, actionAndChanges) {
        var type = actionAndChanges.type, changes = actionAndChanges.changes;
        // this prevents the menu from being closed when the user selects an item with 'Enter' or mouse
        switch (type) {
            case useSelect.stateChangeTypes.MenuKeyDownEnter:
            case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
            case useSelect.stateChangeTypes.ItemClick:
                if (props.type === "multi")
                    return __assign(__assign({}, changes), { isOpen: state.isOpen, highlightedIndex: state.highlightedIndex, inputValue: "" });
                return changes;
            default:
                return changes; // otherwise business as usual.
        }
    }, [props.type]);
    var _h = useMultipleSelection({
        selectedItems: selectedItemsState,
        onSelectedItemsChange: function (_a) {
            var newSelectedItems = _a.selectedItems;
            if (minimumSelectedItems &&
                newSelectedItems &&
                newSelectedItems.length < minimumSelectedItems) {
                return;
            }
            var deduped = newSelectedItems === null || newSelectedItems === void 0 ? void 0 : newSelectedItems.reduce(function (arr, curr) {
                if (!arr.some(function (v) { return v.value === curr.value; }))
                    arr.push(curr);
                return arr;
            }, []);
            setSelectedItemsState(deduped || []);
            handleSelectedItemsChange && handleSelectedItemsChange(deduped);
        },
    }), getDropdownProps = _h.getDropdownProps, addSelectedItem = _h.addSelectedItem, removeSelectedItem = _h.removeSelectedItem, selectedItems = _h.selectedItems, setSelectedItems = _h.setSelectedItems;
    var _j = useSelect({
        stateReducer: stateReducer,
        items: items,
        initialSelectedItem: initialSelectedItem,
        onSelectedItemChange: function (_a) {
            var selected = _a.selectedItem;
            if (props.type === "single") {
                handleSelectedItemChange && handleSelectedItemChange(selected);
            }
        },
        onStateChange: function (_a) {
            var type = _a.type, selected = _a.selectedItem;
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    if (selected) {
                        var existingItem = selectedItems.find(function (i) { return i.value === selected.value; });
                        if (existingItem) {
                            removeSelectedItem(selected);
                        }
                        else {
                            addSelectedItem(selected);
                        }
                    }
                    break;
            }
        },
    }), isOpen = _j.isOpen, selectedItem = _j.selectedItem, getMenuProps = _j.getMenuProps, highlightedIndex = _j.highlightedIndex, getItemProps = _j.getItemProps, selectItem = _j.selectItem, getToggleButtonProps = _j.getToggleButtonProps, closeMenu = _j.closeMenu;
    useEffect(function () {
        if (selectedItemProp !== undefined)
            selectItem(selectedItemProp);
    }, [selectItem, selectedItemProp]);
    useEffect(function () {
        if (selectedItemsProp)
            setSelectedItems(selectedItemsProp);
    }, [selectedItemsProp, setSelectedItems]);
    useEffect(function () {
        if (disabled)
            closeMenu();
    }, [disabled, closeMenu]);
    React__default.useEffect(function () {
        getMenuProps({}, { suppressRefError: true });
    }, [getMenuProps]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Popover, { isOpen: isOpen, onClose: closeMenu, placement: "bottom", size: popoverSize, content: function (isOpenAndAnimating) { return (React__default.createElement(WithFocusTrap, { trapFocus: trapFocus, active: isOpenAndAnimating },
                React__default.createElement("ul", __assign({ className: selectStyles.menu }, getMenuProps({}, { suppressRefError: true }), { "data-testid": testId && "".concat(testId, "-popover-menu") }), items.map(function (item, index) {
                    var _a, _b;
                    return (React__default.createElement("li", __assign({ className: classNames(selectStyles.item, (_a = {},
                            _a[selectStyles.highlight] = highlightedIndex === index,
                            _a), (_b = {},
                            _b[selectStyles.selected] = props.type === "single" &&
                                (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) === item.value,
                            _b)), key: "".concat(item).concat(index), "data-testid": testId && "".concat(testId, "-popover-item-").concat(index) }, getItemProps({
                        item: item,
                        index: index,
                    }), { onClick: function () {
                            if (props.type === "single") {
                                selectItem(item);
                                closeMenu();
                                return;
                            }
                            var existingItem = selectedItems.find(function (i) { return i.value === item.value; });
                            if (existingItem) {
                                removeSelectedItem(item);
                            }
                            else {
                                addSelectedItem(item);
                            }
                        }, tabIndex: "0" }),
                        React__default.createElement("span", { className: classNames(styles.extraLabel, itemClassName) },
                            props.type === "multi" ? (React__default.createElement(Checkbox, { checked: selectedItems
                                    .map(function (i) { return i.value; })
                                    .includes(item.value), id: "multi-select-".concat(index), "data-testid": "multi-select-check-".concat(index), variant: "small", wrapperClassName: styles.checkbox, disabled: selectedItems
                                    .map(function (i) { return i.value; })
                                    .includes(item.value) &&
                                    selectedItems.length <= (minimumSelectedItems || 0) })) : undefined,
                            React__default.createElement("div", { className: classNames(styles.labelGroup, labelGroupClassName) },
                                React__default.createElement("span", { className: classNames(itemLabelClassName) }, item.label),
                                React__default.createElement("span", { className: classNames(extraItemLabelClassName) }, item.extraLabel)))));
                })))); } },
            React__default.createElement(Button, __assign({ className: className, ref: ref, variant: "secondary", color: "interface", disabled: disabled, isSkeleton: isSkeleton }, getToggleButtonProps(getDropdownProps({ ref: ref }, { suppressRefError: true }), { suppressRefError: true }), { "data-testid": testId && "".concat(testId, "-button") }),
                React__default.createElement("span", { className: styles.filterButton },
                    React__default.createElement(Typography, { as: "span", className: styles.label, "data-testid": testId && "".concat(testId, "-label") }, label),
                    customSelectedValueContent ? (customSelectedValueContent(selectedItem)) : (React__default.createElement(LabelValues, { selectedItems: props.type === "single"
                            ? selectedItem && [selectedItem]
                            : selectedItems })),
                    React__default.createElement(ArrowDownIcon, { className: classNames(styles.arrow, (_b = {}, _b[styles.rotate] = isOpen, _b)) }))))));
});
var LabelValues = function (_a) {
    var selectedItems = _a.selectedItems;
    if (!(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length)) {
        return React__default.createElement(React__default.Fragment, null);
    }
    var renderItem = function (selectedItem) {
        return typeof selectedItem.label === "string" ? (React__default.createElement(Typography, { as: "p", className: styles.value }, selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.label)) : (React__default.createElement(React__default.Fragment, null, selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.label));
    };
    if (selectedItems.length === 1) {
        return renderItem(selectedItems[0]);
    }
    return (React__default.createElement(Typography, { as: "p", className: styles.value }, selectedItems.length));
};

export { FilterSelect as F };
